(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function initCarousel(root) {
    const track = root.querySelector("[data-fw-track]");
    const originals = Array.from(root.querySelectorAll("[data-fw-slide]:not([data-fw-clone])"));
    const dots = Array.from(root.querySelectorAll("[data-fw-dot]"));
    const prev = root.querySelector("[data-fw-prev]");
    const next = root.querySelector("[data-fw-next]");
    if (!track || originals.length === 0) return;

    if (root.dataset.fwCarouselBound === "true") return;
    root.dataset.fwCarouselBound = "true";

    /* Seamless strip: clones + originals + clones → …341234123… */
    if (!track.dataset.fwLoopReady) {
      track.querySelectorAll("[data-fw-clone]").forEach((node) => node.remove());
      const beforeFrag = document.createDocumentFragment();
      const afterFrag = document.createDocumentFragment();
      originals.forEach((slide) => {
        const before = slide.cloneNode(true);
        const after = slide.cloneNode(true);
        [before, after].forEach((clone) => {
          clone.setAttribute("data-fw-clone", "true");
          clone.setAttribute("aria-hidden", "true");
          clone.querySelectorAll("a").forEach((a) => a.setAttribute("tabindex", "-1"));
        });
        beforeFrag.appendChild(before);
        afterFrag.appendChild(after);
      });
      track.insertBefore(beforeFrag, track.firstChild);
      track.appendChild(afterFrag);
      track.dataset.fwLoopReady = "true";
    }

    const slides = Array.from(track.querySelectorAll("[data-fw-slide]"));
    const setSize = originals.length;
    let index = setSize;
    let isAnimating = false;
    let pendingEnd = null;
    let snapTimer = 0;

    const logicalIndex = () => ((index % setSize) + setSize) % setSize;

    const clearPending = () => {
      if (pendingEnd) {
        track.removeEventListener("transitionend", pendingEnd);
        pendingEnd = null;
      }
      if (snapTimer) {
        window.clearTimeout(snapTimer);
        snapTimer = 0;
      }
    };

    const paint = (instant) => {
      if (instant || reduceMotion) {
        track.classList.add("is-dragging");
        track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
        void track.offsetWidth;
        requestAnimationFrame(() => track.classList.remove("is-dragging"));
      } else {
        track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      }
    };

    const syncUi = () => {
      const logical = logicalIndex();
      dots.forEach((dot, di) => {
        const active = di === logical;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-current", active ? "true" : "false");
      });
      slides.forEach((slide, si) => {
        slide.setAttribute("aria-hidden", si === index ? "false" : "true");
      });
    };

    const normalizeIndex = () => {
      if (index >= setSize * 2) {
        index -= setSize;
        return true;
      }
      if (index < setSize) {
        index += setSize;
        return true;
      }
      return false;
    };

    const snapIfNeeded = () => {
      if (!normalizeIndex()) return;
      paint(true);
      syncUi();
    };

    const go = (i, instant = false) => {
      if (!instant && isAnimating) return;
      clearPending();
      index = i;
      syncUi();
      paint(instant || reduceMotion);

      if (instant || reduceMotion) {
        isAnimating = false;
        snapIfNeeded();
        return;
      }

      isAnimating = true;
      const finish = (event) => {
        if (event && event.target !== track) return;
        if (event && event.propertyName && event.propertyName !== "transform") return;
        if (!isAnimating) return;
        isAnimating = false;
        clearPending();
        snapIfNeeded();
      };
      pendingEnd = finish;
      track.addEventListener("transitionend", finish);
      snapTimer = window.setTimeout(() => finish(), 600);
    };

    prev?.addEventListener("click", () => go(index - 1));
    next?.addEventListener("click", () => go(index + 1));
    dots.forEach((dot, di) =>
      dot.addEventListener("click", () => go(setSize + di))
    );

    root.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") go(index - 1);
      if (e.key === "ArrowRight") go(index + 1);
    });

    go(setSize, true);
  }

  function initDepartments(root) {
    const viewport = root.querySelector("[data-fw-dept-viewport]");
    const track = root.querySelector("[data-fw-dept-track]");
    const prev = root.querySelector("[data-fw-dept-prev]");
    const next = root.querySelector("[data-fw-dept-next]");
    if (!viewport || !track) return;

    if (root.dataset.fwDeptBound === "true") return;
    root.dataset.fwDeptBound = "true";

    /* Finite strip only — no clones, so index 0 never wraps 09 to the left */
    track.querySelectorAll("[data-fw-clone]").forEach((node) => node.remove());
    track.dataset.fwLoopReady = "";

    const cards = Array.from(track.querySelectorAll("[data-fw-dept-card]"));
    if (cards.length === 0) return;

    const total = cards.length;
    let index = Math.max(0, Math.min(total - 1, Number(root.dataset.fwDeptStart || 0)));
    let dragStartX = 0;
    let dragDelta = 0;
    let isDragging = false;
    let baseOffset = 0;
    let isAnimating = false;

    const getCenteredOffset = (i) => {
      const card = cards[i];
      if (!card) return 0;
      return card.offsetLeft + card.offsetWidth / 2 - viewport.clientWidth / 2;
    };

    const syncClasses = () => {
      cards.forEach((card, i) => {
        const rel = i - index;
        card.classList.toggle("is-active", rel === 0);
        card.classList.toggle("is-side-prev", rel === -1);
        card.classList.toggle("is-side-next", rel === 1);
        card.classList.toggle("is-passed", rel < -1);
        card.classList.toggle("is-ahead", rel > 1);
        card.setAttribute("aria-hidden", rel === 0 ? "false" : "true");
      });

      const hasPrevious = index > 0;
      const hasNext = index < total - 1;
      if (prev) {
        prev.disabled = !hasPrevious;
        prev.setAttribute("aria-disabled", hasPrevious ? "false" : "true");
      }
      if (next) {
        next.disabled = !hasNext;
        next.setAttribute("aria-disabled", hasNext ? "false" : "true");
      }
    };

    const paint = (instant) => {
      baseOffset = getCenteredOffset(index);
      if (instant || reduceMotion) {
        track.classList.add("is-dragging");
        track.style.transform = `translate3d(${-baseOffset}px, 0, 0)`;
        void track.offsetWidth;
        requestAnimationFrame(() => track.classList.remove("is-dragging"));
      } else {
        track.style.transform = `translate3d(${-baseOffset}px, 0, 0)`;
      }
    };

    const go = (i, instant = false) => {
      const nextIndex = Math.max(0, Math.min(total - 1, i));
      if (!instant && isAnimating) return;
      if (!instant && nextIndex === index) return;

      index = nextIndex;
      syncClasses();
      paint(instant || reduceMotion);

      if (instant || reduceMotion) {
        isAnimating = false;
        root.classList.add("is-ready");
        return;
      }

      isAnimating = true;
      const finish = (event) => {
        if (event && event.target !== track) return;
        if (event && event.propertyName && event.propertyName !== "transform") return;
        track.removeEventListener("transitionend", finish);
        window.clearTimeout(finish.timer);
        isAnimating = false;
      };
      track.addEventListener("transitionend", finish);
      finish.timer = window.setTimeout(() => finish(), 650);
    };

    prev?.addEventListener("click", () => {
      if (index > 0) go(index - 1);
    });
    next?.addEventListener("click", () => {
      if (index < total - 1) go(index + 1);
    });

    const onPointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      isDragging = true;
      dragStartX = event.clientX;
      dragDelta = 0;
      baseOffset = getCenteredOffset(index);
      track.classList.add("is-dragging");
      viewport.classList.add("is-dragging");
      viewport.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!isDragging) return;
      dragDelta = event.clientX - dragStartX;
      track.style.transform = `translate3d(${-(baseOffset - dragDelta)}px, 0, 0)`;
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove("is-dragging");
      viewport.classList.remove("is-dragging");
      const threshold = Math.min(120, cards[index].offsetWidth * 0.18);
      if (dragDelta > threshold && index > 0) go(index - 1);
      else if (dragDelta < -threshold && index < total - 1) go(index + 1);
      else go(index, true);
      dragDelta = 0;
    };

    viewport.addEventListener("pointerdown", onPointerDown);
    viewport.addEventListener("pointermove", onPointerMove);
    viewport.addEventListener("pointerup", onPointerUp);
    viewport.addEventListener("pointercancel", onPointerUp);
    viewport.addEventListener("pointerleave", () => {
      if (isDragging) onPointerUp();
    });

    window.addEventListener(
      "resize",
      () => {
        go(index, true);
      },
      { passive: true }
    );

    /* Start at 01 Produce — matches Liquid first-paint classes */
    go(index, true);
    requestAnimationFrame(() => go(index, true));
  }

  function initCuisines(root) {
    if (root.dataset.fwCuisineBound === "true") return;
    root.dataset.fwCuisineBound = "true";

    const buttons = Array.from(root.querySelectorAll("[data-fw-cuisine]"));
    const cards = Array.from(root.querySelectorAll("[data-fw-cuisine-panel]"));
    if (!buttons.length || !cards.length) return;

    const total = cards.length;

    const setLinkTabIndex = (card, enabled) => {
      card.querySelectorAll("a").forEach((link) => {
        if (enabled) link.removeAttribute("tabindex");
        else link.setAttribute("tabindex", "-1");
      });
    };

    let activeId = null;

    const syncCuisineNav = () => {
      const activeIndex = buttons.findIndex((btn) => btn.classList.contains("is-active"));
      if (activeIndex < 0) return;

      const currentEl = root.querySelector("[data-fw-cuisine-current]");
      const labelEl = root.querySelector("[data-fw-cuisine-status-label]");
      if (currentEl) currentEl.textContent = String(activeIndex + 1);

      if (labelEl) {
        const name = buttons[activeIndex]?.querySelector(".fw-cuisines__name")?.textContent?.trim();
        labelEl.textContent = name || "";
      }
    };

    const goRelative = (delta) => {
      const activeIndex = buttons.findIndex((btn) => btn.classList.contains("is-active"));
      if (activeIndex < 0) return;
      const nextIndex =
        delta > 0
          ? (activeIndex + 1) % buttons.length
          : (activeIndex - 1 + buttons.length) % buttons.length;
      const id = buttons[nextIndex]?.getAttribute("data-fw-cuisine");
      if (id) activate(id);
    };

    const activate = (id, instant = false) => {
      const activeIndex = cards.findIndex(
        (card) => card.getAttribute("data-fw-cuisine-panel") === id
      );
      if (activeIndex < 0 || id === activeId) return;

      const prevId = activeId;
      activeId = id;

      buttons.forEach((btn) => {
        const on = btn.getAttribute("data-fw-cuisine") === id;
        btn.classList.toggle("is-active", on);
        btn.setAttribute("aria-selected", on ? "true" : "false");
      });

      const applyStack = () => {
        cards.forEach((card, i) => {
          const depth = (i - activeIndex + total) % total;
          const on = i === activeIndex;
          card.dataset.stack = String(depth);
          card.classList.toggle("is-active", on);
          card.classList.remove("is-fading");
          card.setAttribute("aria-hidden", on ? "false" : "true");
          setLinkTabIndex(card, on);
        });
        syncCuisineNav();
      };

      if (instant || reduceMotion || !prevId) {
        applyStack();
        return;
      }

      /* Light fade while cards reorder in the stack */
      cards.forEach((card) => card.classList.add("is-fading"));
      window.requestAnimationFrame(() => {
        applyStack();
      });
    };

    const hoverMode = root.dataset.fwCuisineHover === "true";

    buttons.forEach((btn) => {
      const id = btn.getAttribute("data-fw-cuisine");
      if (hoverMode) {
        btn.addEventListener("pointerenter", () => activate(id));
        btn.addEventListener("focus", () => activate(id));
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          activate(id);
        });
      } else {
        btn.addEventListener("click", () => activate(id));
      }
    });

    const first = buttons[0]?.getAttribute("data-fw-cuisine");
    if (first) activate(first, true);

    root.querySelector("[data-fw-cuisine-prev]")?.addEventListener("click", () => goRelative(-1));
    root.querySelector("[data-fw-cuisine-next]")?.addEventListener("click", () => goRelative(1));

    const stack = root.querySelector("[data-fw-cuisine-stack]");
    const compactCuisineMq = window.matchMedia("(max-width: 1024px)");
    if (stack && compactCuisineMq.matches) {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      stack.addEventListener(
        "touchstart",
        (event) => {
          if (event.touches.length !== 1) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      stack.addEventListener(
        "touchend",
        (event) => {
          if (!tracking) return;
          tracking = false;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
          goRelative(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );

      let pointerDown = false;
      stack.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0) return;
        pointerDown = true;
        startX = event.clientX;
        stack.classList.add("is-dragging");
      });

      stack.addEventListener("pointerup", (event) => {
        if (!pointerDown) return;
        pointerDown = false;
        stack.classList.remove("is-dragging");
        const dx = event.clientX - startX;
        if (Math.abs(dx) < 48) return;
        goRelative(dx < 0 ? 1 : -1);
      });

      stack.addEventListener("pointercancel", () => {
        pointerDown = false;
        stack.classList.remove("is-dragging");
      });
    }
  }

  function initFaq(root) {
    root.querySelectorAll("[data-fw-faq-item]").forEach((item) => {
      const button = item.querySelector("[data-fw-faq-toggle]");
      const panel = item.querySelector("[data-fw-faq-panel]");
      if (!button || !panel) return;

      button.addEventListener("click", () => {
        const open = item.classList.toggle("is-open");
        button.setAttribute("aria-expanded", open ? "true" : "false");
        panel.hidden = !open;
      });
    });
  }

  function parseCounterValue(raw) {
    const text = String(raw || "").trim();
    const match = text.match(/^([^\d.-]*)(-?[\d,]+(?:\.\d+)?)(.*)$/);
    if (!match) return null;

    const prefix = match[1] || "";
    const numeric = match[2].replace(/,/g, "");
    const suffix = match[3] || "";
    const target = Number(numeric);
    if (!Number.isFinite(target)) return null;

    const decimals = (numeric.split(".")[1] || "").length;
    const useComma = match[2].includes(",");

    return { prefix, suffix, target, decimals, useComma };
  }

  function formatCounter(value, config) {
    let num = config.decimals
      ? value.toFixed(config.decimals)
      : String(Math.round(value));

    if (config.useComma) {
      const parts = num.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      num = parts.join(".");
    }

    return `${config.prefix}${num}${config.suffix}`;
  }

  function animateCounter(el, config, duration = 1400) {
    if (reduceMotion) {
      el.textContent = formatCounter(config.target, config);
      return;
    }

    const start = performance.now();
    const from = 0;

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = from + (config.target - from) * eased;
      el.textContent = formatCounter(current, config);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  function initStatsCounters(root) {
    const values = Array.from(root.querySelectorAll("[data-fw-counter]"));
    if (!values.length) return;

    const run = () => {
      values.forEach((el, index) => {
        if (el.dataset.fwCounted === "true") return;
        const config = parseCounterValue(el.getAttribute("data-fw-counter"));
        if (!config) return;
        el.dataset.fwCounted = "true";
        el.textContent = formatCounter(0, config);
        setTimeout(() => animateCounter(el, config, 1400 + index * 120), index * 80);
      });
    };

    if (!("IntersectionObserver" in window)) {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            run();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );

    observer.observe(root);
  }

  const bootHome = () => {
    document.body.classList.add("fw-home");
    document.querySelectorAll("[data-fw-order-ahead]").forEach(initCarousel);
    document.querySelectorAll("[data-fw-departments]").forEach(initDepartments);
    document.querySelectorAll("[data-fw-cuisines]").forEach(initCuisines);
    document.querySelectorAll("[data-fw-faq]").forEach(initFaq);
    document.querySelectorAll("[data-fw-stats]").forEach(initStatsCounters);
  };

  document.addEventListener("DOMContentLoaded", bootHome);

  /* Theme Editor section reload */
  document.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    section.querySelectorAll("[data-fw-departments]").forEach((root) => {
      root.dataset.fwDeptBound = "";
      root.classList.remove("is-ready");
      initDepartments(root);
    });
    section.querySelectorAll("[data-fw-order-ahead]").forEach((root) => {
      root.dataset.fwCarouselBound = "";
      const track = root.querySelector("[data-fw-track]");
      if (track) {
        track.dataset.fwLoopReady = "";
        track.querySelectorAll("[data-fw-clone]").forEach((node) => node.remove());
      }
      initCarousel(root);
    });
    section.querySelectorAll("[data-fw-cuisines]").forEach((root) => {
      root.dataset.fwCuisineBound = "";
      initCuisines(root);
    });
    section.querySelectorAll("[data-fw-faq]").forEach(initFaq);
    section.querySelectorAll("[data-fw-stats]").forEach(initStatsCounters);
  });
})();
